import * as React from 'react';
import classNames from 'classnames';
import defaultImage from '@/assets/images/defaultImage.jpg';
import { useDamFile } from '@/contexts/DamFileProvider';
import { MediaSet } from '@/types/media';
import styles from './DamVideo.module.scss';
import { DamVideoComponentProps } from './register';

const fallBackImage: MediaSet = {
	size180: { url: defaultImage },
	size320: { url: defaultImage },
	size480: { url: defaultImage },
	size720: { url: defaultImage },
	size960: { url: defaultImage },
	size1280: { url: defaultImage },
	size1920: { url: defaultImage },
};

export interface DamVideoProps extends DamVideoComponentProps {
	readonly unsupportedVideoMessage?: string;
}
const defaultUnsupportedVideoMessage =
	"Sorry, your browser doesn't support embedded videos, but don't worry, you can download the video " +
	'and watch it with your preferred video player!';

/**
 * CMS Video component, used for adding video from the DAM. It is used instead of
 * Builder's default Video component with similar functionality.
 * */
export const DamVideo: React.FunctionComponent<DamVideoProps> = ({
	children,
	video,
	posterImage,
	autoPlay,
	controls,
	muted,
	loop,
	objectFit,
	videoPosition,
	aspectRatio,
	attributes,
	unsupportedVideoMessage = defaultUnsupportedVideoMessage,
}) => {
	const damVideo = useDamFile(video ?? null);
	const damPoster = useDamFile(posterImage ?? null) ?? fallBackImage;

	const { className: builderClassNames, ...restOfAttributes } = attributes ?? {};

	return (
		<div
			className={classNames(styles.wrapper, { [styles.hasChildren]: children }, builderClassNames)}
			{...restOfAttributes}
			key={'damVideo'}
			style={
				{
					'--objectFit': objectFit ? objectFit : 'cover',
					'--objectPosX': videoPosition?.x ? `${videoPosition.x}%` : 'center',
					'--objectPosY': videoPosition?.y ? `${videoPosition.y}%` : 'center',
					'--aspectRatio': aspectRatio ? aspectRatio : '0',
				} as React.CSSProperties
			}
		>
			{/* TODO: add support for captions in CCJENSEN-909 */}
			{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
			<video
				className={styles.video}
				width={damVideo?.size180?.width || undefined}
				controls={controls || undefined}
				autoPlay={autoPlay || undefined}
				muted={muted || undefined}
				loop={loop || undefined}
				poster={damPoster.size960?.url || undefined}
			>
				{damVideo?.size180 && <source src={damVideo.size180.url} type="video/mp4" />}

				<p>
					<a href={damVideo?.size180?.url} download>
						{unsupportedVideoMessage}
					</a>
				</p>
			</video>
			{children}
		</div>
	);
};
